import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import type { LeadSearchFilters } from '../types/leadSearch';
import type { UserReference, InsuranceCompanyReference, DamageCategoryReference, LeadStatus } from '../types';
import type { SubcontractorLookupDto } from '@/features/subcontractors/types';
import { getStates } from '../api/leads-api';

interface LeadSearchFormProps {
  filters: LeadSearchFilters;
  onFiltersChange: (filters: LeadSearchFilters) => void;
  onSearch: () => void;
  onClear: () => void;
  isLoading: boolean;
  users: UserReference[];
  insuranceCompanies: InsuranceCompanyReference[];
  damageCategories: DamageCategoryReference[];
  subcontractors: SubcontractorLookupDto[];
}

const statuses: LeadStatus[] = ['Unassigned', 'Assigned', 'Sold', 'NoGo', 'Closed'];

export function LeadSearchForm({
  filters,
  onFiltersChange,
  onSearch,
  onClear,
  isLoading,
  users,
  insuranceCompanies,
  damageCategories,
  subcontractors,
}: LeadSearchFormProps) {
  const [customerInfoOpen, setCustomerInfoOpen] = useState(true);
  const [propertyAddressOpen, setPropertyAddressOpen] = useState(false);
  const [billingAddressOpen, setBillingAddressOpen] = useState(false);
  const [jobInfoOpen, setJobInfoOpen] = useState(false);
  const [datesOpen, setDatesOpen] = useState(false);
  const [assignmentOpen, setAssignmentOpen] = useState(false);

  const states = getStates();

  const handleInputChange = (field: keyof LeadSearchFilters, value: string) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    });
  };

  const handleSelectChange = (
    field: keyof LeadSearchFilters,
    value: string | number | undefined
  ) => {
    onFiltersChange({
      ...filters,
      [field]: value || undefined,
    });
  };

  return (
    <div className="bg-card rounded-[20px] p-6 space-y-4">
      {/* Customer Info Section */}
      <Collapsible open={customerInfoOpen} onOpenChange={setCustomerInfoOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {customerInfoOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Customer Info
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={filters.firstName || ''}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                placeholder="Enter first name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={filters.lastName || ''}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                placeholder="Enter last name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="companyName">Company</Label>
              <Input
                id="companyName"
                value={filters.companyName || ''}
                onChange={(e) => handleInputChange('companyName', e.target.value)}
                placeholder="Enter company name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                value={filters.phone || ''}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="Enter phone number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={filters.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                placeholder="Enter email address"
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Property Address Section */}
      <Collapsible open={propertyAddressOpen} onOpenChange={setPropertyAddressOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {propertyAddressOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Property Address
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2 lg:col-span-2">
              <Label htmlFor="propertyAddress">Street</Label>
              <Input
                id="propertyAddress"
                value={filters.propertyAddress || ''}
                onChange={(e) => handleInputChange('propertyAddress', e.target.value)}
                placeholder="Enter street address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="propertyCity">City</Label>
              <Input
                id="propertyCity"
                value={filters.propertyCity || ''}
                onChange={(e) => handleInputChange('propertyCity', e.target.value)}
                placeholder="Enter city"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="propertyState">State</Label>
              <Select
                id="propertyState"
                value={filters.propertyState || ''}
                onChange={(e) => handleSelectChange('propertyState', e.target.value)}
              >
                <option value="">Select state</option>
                {states.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="propertyZip">Zip</Label>
              <Input
                id="propertyZip"
                value={filters.propertyZip || ''}
                onChange={(e) => handleInputChange('propertyZip', e.target.value)}
                placeholder="Enter zip code"
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Billing Address Section */}
      <Collapsible open={billingAddressOpen} onOpenChange={setBillingAddressOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {billingAddressOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Billing Address
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2 lg:col-span-2">
              <Label htmlFor="billingAddress">Street</Label>
              <Input
                id="billingAddress"
                value={filters.billingAddress || ''}
                onChange={(e) => handleInputChange('billingAddress', e.target.value)}
                placeholder="Enter street address"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billingCity">City</Label>
              <Input
                id="billingCity"
                value={filters.billingCity || ''}
                onChange={(e) => handleInputChange('billingCity', e.target.value)}
                placeholder="Enter city"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="billingState">State</Label>
              <Select
                id="billingState"
                value={filters.billingState || ''}
                onChange={(e) => handleSelectChange('billingState', e.target.value)}
              >
                <option value="">Select state</option>
                {states.map((state) => (
                  <option key={state.code} value={state.code}>
                    {state.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="billingZip">Zip</Label>
              <Input
                id="billingZip"
                value={filters.billingZip || ''}
                onChange={(e) => handleInputChange('billingZip', e.target.value)}
                placeholder="Enter zip code"
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Job Info Section */}
      <Collapsible open={jobInfoOpen} onOpenChange={setJobInfoOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {jobInfoOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Job Info
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="jobNumber">Job Number</Label>
              <Input
                id="jobNumber"
                value={filters.jobNumber || ''}
                onChange={(e) => handleInputChange('jobNumber', e.target.value)}
                placeholder="Enter job number"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                id="status"
                value={filters.status || ''}
                onChange={(e) => handleSelectChange('status', e.target.value as LeadStatus)}
              >
                <option value="">All statuses</option>
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="damageType">Damage Type</Label>
              <Select
                id="damageType"
                value={filters.damageType || ''}
                onChange={(e) => handleSelectChange('damageType', e.target.value)}
              >
                <option value="">All types</option>
                {damageCategories.map((category) => (
                  <option key={category.id} value={category.name}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Dates Section */}
      <Collapsible open={datesOpen} onOpenChange={setDatesOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {datesOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Dates
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="leadDateStart">Lead Date From</Label>
              <Input
                id="leadDateStart"
                type="date"
                value={filters.leadDateStart || ''}
                onChange={(e) => handleInputChange('leadDateStart', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="leadDateEnd">Lead Date To</Label>
              <Input
                id="leadDateEnd"
                type="date"
                value={filters.leadDateEnd || ''}
                onChange={(e) => handleInputChange('leadDateEnd', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="openedDateStart">Opened Date From</Label>
              <Input
                id="openedDateStart"
                type="date"
                value={filters.openedDateStart || ''}
                onChange={(e) => handleInputChange('openedDateStart', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="openedDateEnd">Opened Date To</Label>
              <Input
                id="openedDateEnd"
                type="date"
                value={filters.openedDateEnd || ''}
                onChange={(e) => handleInputChange('openedDateEnd', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="closedDateStart">Closed Date From</Label>
              <Input
                id="closedDateStart"
                type="date"
                value={filters.closedDateStart || ''}
                onChange={(e) => handleInputChange('closedDateStart', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="closedDateEnd">Closed Date To</Label>
              <Input
                id="closedDateEnd"
                type="date"
                value={filters.closedDateEnd || ''}
                onChange={(e) => handleInputChange('closedDateEnd', e.target.value)}
              />
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Assignment Section */}
      <Collapsible open={assignmentOpen} onOpenChange={setAssignmentOpen}>
        <CollapsibleTrigger className="flex items-center gap-2 w-full text-left font-semibold text-lg">
          {assignmentOpen ? (
            <ChevronDown className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
          Assignment
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="assignedToUserId">Estimator</Label>
              <Select
                id="assignedToUserId"
                value={filters.assignedToUserId?.toString() || ''}
                onChange={(e) =>
                  handleSelectChange(
                    'assignedToUserId',
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
              >
                <option value="">All estimators</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="insuranceCompanyId">Insurance Company</Label>
              <Select
                id="insuranceCompanyId"
                value={filters.insuranceCompanyId?.toString() || ''}
                onChange={(e) =>
                  handleSelectChange(
                    'insuranceCompanyId',
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
              >
                <option value="">All companies</option>
                {insuranceCompanies.map((company) => (
                  <option key={company.id} value={company.id}>
                    {company.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="subcontractorId">Subcontractor</Label>
              <Select
                id="subcontractorId"
                value={filters.subcontractorId?.toString() || ''}
                onChange={(e) =>
                  handleSelectChange(
                    'subcontractorId',
                    e.target.value ? Number(e.target.value) : undefined
                  )
                }
              >
                <option value="">All subcontractors</option>
                {subcontractors.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.companyName}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4 border-t">
        <Button onClick={onSearch} disabled={isLoading} className="min-w-[120px]">
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isLoading ? 'Searching...' : 'Search'}
        </Button>
        <Button onClick={onClear} disabled={isLoading} variant="outline" className="min-w-[120px]">
          Clear
        </Button>
      </div>
    </div>
  );
}
